import React, { useCallback, useState } from 'react';
import { Box, Info, QuestionCircle } from './core';
import { Popover, IPopoverProps } from './Popover';
import type { BoxProps } from 'theme-ui';

interface ITooltipProps extends Omit<IPopoverProps, 'content'> {
  text: React.ReactNode;
  width?: number | 'auto';
}

const DEFAULT_WIDTH = 250;

interface ITooltipContentProps extends IPopoverProps {
  onOpen?: () => void;
  width?: number | 'auto';
  wrap?: boolean; // whether or not to wrap content in TooltipContainer
}

export enum IIcons {
  INFO = 'INFO',
  HELP = 'HELP',
}

export const TooltipIcon = ({
  icon = IIcons.INFO,
  ...rest
}: {
  icon?: IIcons;
}): JSX.Element | null => {
  const sx = {
    width: '.8em',
    height: '.8em',
    mx: '.1em',
    mb: '-.04em',
  };
  switch (icon) {
    case IIcons.INFO:
      return <Info sx={sx} {...rest} />;
    case IIcons.HELP:
      return <QuestionCircle sx={sx} {...rest} />;
    default:
      return null;
  }
};

interface ITooltipContainer extends BoxProps {
  width?: number | string;
}

export const TooltipContainer = ({
  width = 'auto',
  ...rest
}: ITooltipContainer): JSX.Element => (
  <Box
    {...rest}
    sx={{
      width: width === 'auto' ? 'auto' : `${width}px`,
      maxWidth: '90vw',
      p: '1rem 1rem',
      wordBreak: 'break-word',
      backgroundColor: 'background',
      borderRadius: '4px',
      border: '1px solid black',
      borderColor: 'muted',
      boxShadow: '0 4px 8px 0 rgba(0,0,0,.15)',
      ...rest.sx,
    }}
  />
);

export const Tooltip = ({
  text,
  width = DEFAULT_WIDTH,
  ...rest
}: ITooltipProps): JSX.Element => (
  <Popover
    content={<TooltipContainer width={width}>{text}</TooltipContainer>}
    {...rest}
  />
);

function TooltipContent({
  content,
  wrap = false,
  width = DEFAULT_WIDTH,
  ...rest
}: ITooltipContentProps) {
  return (
    <Popover
      content={
        wrap ? (
          <TooltipContainer width={width}>{content}</TooltipContainer>
        ) : (
          content
        )
      }
      {...rest}
    />
  );
}

export function MouseoverTooltip({
  children,
  ...rest
}: Omit<ITooltipProps, 'show'>): JSX.Element {
  const [show, setShow] = useState(false);
  const open = useCallback(() => setShow(true), [setShow]);
  const close = useCallback(() => setShow(false), [setShow]);
  const toggle = useCallback(() => setShow(!show), [setShow, show]);
  return (
    <Box
      sx={{ display: 'inline-block' }}
      onMouseEnter={open}
      onMouseLeave={close}
      onTouchStart={toggle}
      onTouchCancel={close}
    >
      <Tooltip {...rest} show={show}>
        <Box>{children}</Box>
      </Tooltip>
    </Box>
  );
}

export function MouseoverTooltipContent({
  content,
  children,
  onOpen: openCallback = undefined,
  ...rest
}: Omit<ITooltipContentProps, 'show'>): JSX.Element {
  const [show, setShow] = useState(false);
  const open = useCallback(() => {
    setShow(true);
    openCallback?.();
  }, [openCallback]);
  const close = useCallback(() => setShow(false), [setShow]);
  const toggle = useCallback(() => setShow(!show), [setShow, show]);
  return (
    <Box
      sx={{ display: 'inline-block' }}
      onMouseEnter={open}
      onMouseLeave={close}
      onTouchStart={toggle}
      onTouchCancel={close}
    >
      <TooltipContent {...rest} show={show} content={content}>
        <Box onMouseLeave={close} onTouchCancel={close}>
          {children}
        </Box>
      </TooltipContent>
    </Box>
  );
}
